import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

import BuildDuration from 'app/components/build/Duration';
import BuildMessage from 'app/components/build/Message';
import BuildStatusDescription from 'app/components/shared/BuildStatusDescription';
import Emojify from 'app/components/shared/Emojify';
import Icon from 'app/components/shared/Icon';
import RemoteButtonComponent from 'app/components/shared/RemoteButtonComponent';
import UserAvatar from 'app/components/shared/UserAvatar';

import { shortCommit } from 'app/lib/commits';

import Pipeline from './pipeline';

class BuildHeaderComponent extends React.PureComponent {
  static propTypes = {
    build: PropTypes.shape({
      number: PropTypes.number.isRequired,
      state: PropTypes.string.isRequired,
      blockedState: PropTypes.string,
      finishedAt: PropTypes.string,
      source: PropTypes.string.isRequired,
      message: PropTypes.string,
      commitId: PropTypes.string,
      commitShortLength: PropTypes.number,
      commitUrl: PropTypes.string,
      branchName: PropTypes.string,
      branchPath: PropTypes.string,
      authorName: PropTypes.string,
      authorAvatar: PropTypes.string,
      authorUuid: PropTypes.string,
      path: PropTypes.string.isRequired,
      project: PropTypes.shape({
        name: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
        public: PropTypes.bool.isRequired,
        provider: PropTypes.shape({
          id: PropTypes.string
        }).isRequired
      }).isRequired,
      pullRequest: PropTypes.shape({
        id: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired
      }),
      account: PropTypes.shape({
        name: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired
      }).isRequired,
      rebuildPath: PropTypes.string,
      rebuiltFrom: PropTypes.shape({
        url: PropTypes.string.isRequired,
        number: PropTypes.number.isRequired
      }),
      cancelPath: PropTypes.string,
      triggeredFrom: PropTypes.shape({
        uuid: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        project: PropTypes.shape({
          name: PropTypes.string.isRequired
        }).isRequired,
        build: PropTypes.shape({
          number: PropTypes.number.isRequired
        }).isRequired
      }),
      pipelineSchedule: PropTypes.shape({
        url: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired
      }),
      permissions: PropTypes.shape({
        rebuild: PropTypes.shape({
          allowed: PropTypes.bool,
          reason: PropTypes.string,
          message: PropTypes.string
        }).isRequired,
        cancel: PropTypes.shape({
          allowed: PropTypes.bool,
          reason: PropTypes.string,
          message: PropTypes.string
        }).isRequired
      }).isRequired
    }).isRequired,
    showRebuild: PropTypes.bool,
    showProject: PropTypes.bool,
    showOrganization: PropTypes.bool
  };

  componentDidMount() {
    ReactDOM.findDOMNode(this).addEventListener('ajax:error', this._onAjaxError); // eslint-disable-line react/no-find-dom-node
  }

  componentWillUnmount() {
    ReactDOM.findDOMNode(this).removeEventListener('ajax:error', this._onAjaxError); // eslint-disable-line react/no-find-dom-node
  }

  _onAjaxError = (event, xhr) => {
    if (xhr.responseJSON != null) {
      alert(xhr.responseJSON['message']);
    }
  };

  bootstrapState = () => {
    switch (this.props.build.state) {
      case 'failed':
      case 'danger':
      case 'canceled':
      case 'canceling':
        return 'danger';
      case 'passed':
      case 'blocked':
        switch (this.props.build.blockedState) {
          case 'failed':
            return 'danger';
          case 'running':
            return 'warning';
          case 'passed':
          default:
            return 'success';
        }
      case 'started':
        return 'warning';
      default:
        return 'default';
    }
  };

  icon = () => {
    switch (this.props.build.state) {
      case 'failed':
        return 'fa fa-times';
      case 'canceled':
        return 'fa fa-warning';
      case 'canceling':
        return 'fa fa-warning';
      case 'passed':
        return 'fa fa-check';
      case 'started':
        return 'fa fa-refresh fa-spin';
      case 'blocked':
        return 'fa fa-pause';
      case 'scheduled':
        return 'fa fa-clock-o';
      case 'skipped':
      case 'not_run':
        return 'fa fa-minus-square';
    }
  };

  buildInfoNode = () => {
    const { build } = this.props;

    let buildNumberNode;
    if (build.path === window.location.pathname) {
      buildNumberNode = `Build #${build.number}`;
    } else {
      buildNumberNode = (
        <a className="color-inherit hover-color-inherit" href={build.path}>
          Build #{build.number}
        </a>
      );
    }

    return (
      <span className="build-header__build-number">
        <span className="number">
          {buildNumberNode}
        </span>
      </span>
    );
  };

  commitInfoNode = () => {
    let branchNode, commitNode, pullRequestNode;
    const { build } = this.props;

    if (build.commitId) {
      if (build.commitUrl) {
        const providerIconClass = `provider-icon fa fa-${build.project.provider.id}`;
        commitNode = (
          <span title={build.commitId}>
            <i className={providerIconClass} />
            <a href={build.commitUrl} className="color-inherit hover-color-inherit">
              {shortCommit(build.commitId, build.commitShortLength)}
            </a>
          </span>
        );
      } else {
        commitNode = (
          <span title={build.commitId}>
            {shortCommit(build.commitId, build.commitShortLength)}
          </span>
        );
      }
    }

    // Do we have a branch?
    if (build.branchName) {
      branchNode = (
        <a className="color-inherit hover-color-inherit" href={build.branchPath}>
          {build.branchName}
        </a>
      );
    }

    if (build.pullRequest) {
      pullRequestNode = (
        <a className="color-inherit hover-color-inherit" href={build.pullRequest.url}>
          Pull Request #{build.pullRequest.id}
        </a>
      );
    }

    if (branchNode && commitNode && pullRequestNode) {
      return (
        <span className="build-header__build-commit">
          <span className="branch">
            {branchNode}
          </span>
          {" "}
          <code className="commit monospace">
            {commitNode}
          </code>
          {" "}
          <span className="pull-request">
            (
            {pullRequestNode}
            )
          </span>
        </span>
      );
    } else if (branchNode && commitNode) {
      return (
        <span className="build-header__build-commit">
          <span className="branch">
            {branchNode}
          </span>
          {" "}
          <code className="commit monospace">
            {commitNode}
          </code>
        </span>
      );
    } else if (commitNode) {
      return (
        <span className="build-header__build-commit">
          <code className="commit monospace">
            {commitNode}
          </code>
        </span>
      );
    }

    return null;
  };

  timeAgoNode = () => {
    return (
      <small className="dark-gray build-secondary-time">
        <BuildStatusDescription build={this.props.build} />
      </small>
    );
  };

  sourceLabel = (source) => {
    switch (source) {
      case 'webhook':
        return 'Webhook';
      case 'ui':
        return 'Web';
      case 'api':
        return 'API';
      case 'trigger_job':
        return 'Pipeline';
      case 'schedule':
        return 'Pipeline Schedule';
      default:
        return 'Unknown';
    }
  };

  metaInformation = () => {
    let rebuiltFromNode;
    let triggeredFromNode;
    let pipelineScheduleNode;

    const sourceNode = (
      <div className="small dark-gray">
        {`Triggered from ${this.sourceLabel(this.props.build.source)}`}
      </div>
    );

    let innerClassName = 'border-left border-mid-gray flex-auto flex flex-column px2';

    if (this.props.build.rebuiltFrom) {
      rebuiltFromNode = (
        <div className="small dark-gray">
          {'Rebuilt from '}
          <a href={this.props.build.rebuiltFrom.url} className="semi-bold color-inherit hover-color-inherit">
            {`#${this.props.build.rebuiltFrom.number}`}
          </a>
        </div>
      );
    }

    if (this.props.build.pipelineSchedule) {
      pipelineScheduleNode = (
        <div className="small dark-gray flex">
          <a href={this.props.build.pipelineSchedule.url} className="semi-bold color-inherit hover-color-inherit flex items-center">
            <Icon icon="schedules" style={{ width: 15, height: 15, marginRight: 3 }} />
            <Emojify text={this.props.build.pipelineSchedule.label} />
          </a>
        </div>
      );
    }

    if (this.props.build.triggeredFrom && this.props.build.triggeredFrom.url) {
      const jobName = this.props.build.triggeredFrom.name || `Job #${this.props.build.triggeredFrom.uuid}`;

      innerClassName += ' truncate';

      triggeredFromNode = (
        <div className="small dark-gray">
          <a href={this.props.build.triggeredFrom.url} className="semi-bold color-inherit hover-color-inherit">
            <span>
              {this.props.build.triggeredFrom.project.name}
              {' - Build #'}
              {this.props.build.triggeredFrom.build.number}
              {' / '}
              <Emojify text={jobName} />
            </span>
          </a>
        </div>
      );
    }

    // This nested structure allows us to have two disconnected borders;
    // one which is always along the top of the flex element, for use in
    // the flex-wrapped layout, and one which is on the left, but only
    // along the height of the actual content. These are overflow-hidden
    // with the negative 1px margins, and the result is a dynamic border
    // dependent upon whether the flex element is wrapped or not!
    return (
      <div
        className="border-top border-gray flex-auto flex items-center py1"
        style={{
          marginTop: -1,
          marginLeft: -1
        }}
      >
        <div className={innerClassName}>
          {sourceNode}
          {rebuiltFromNode}
          {triggeredFromNode}
          {pipelineScheduleNode}
        </div>
      </div>
    );
  };

  render() {
    const { build } = this.props;

    const panelClassName = `panel panel-${this.bootstrapState()} build-panel build-state-${build.state} ${this.props.showProject ? 'mb4' : undefined}`;

    let rebuildNode;

    if (this.props.showRebuild && ((build.state === 'canceling') || build.finishedAt)) {
      if (build.permissions.rebuild.allowed) {
        rebuildNode = (
          <a
            className="btn btn-default build-rebuild-button center mx1"
            data-method="post"
            href={build.rebuildPath}
            rel="nofollow"
            style={{ flexGrow: 1 }}
          >
            <i className="fa fa-refresh mr1" />
            <span>Rebuild</span>
          </a>
        );
      } else if (build.permissions.rebuild.reason !== 'anonymous') {
        rebuildNode = (
          <a
            className="btn btn-default build-rebuild-button center mx1"
            data-method="post"
            disabled={true}
            href={build.rebuildPath}
            aria-label={build.permissions.rebuild.message}
            rel="nofollow"
            style={{ flexGrow: 1 }}
          >
            <i className="fa fa-refresh mr1" />
            <span>Rebuild</span>
          </a>
        );
      }
    }

    let cancelNode;

    if (build.state === 'scheduled' || build.state === 'started' || build.state === 'blocked') {
      if (build.permissions.cancel.allowed) {
        cancelNode = (
          <RemoteButtonComponent
            url={build.cancelPath}
            method="post"
            loadingText="Canceling…"
            className="btn btn-danger build-cancel-button center mx1"
            style={{ flexGrow: 1 }}
          >
            <span>Cancel</span>
          </RemoteButtonComponent>
        );
      } else if (build.permissions.cancel.reason !== 'anonymous') {
        cancelNode = (
          <a
            href="#"
            disabled={true}
            aria-label={build.permissions.cancel.message}
            className="btn btn-danger build-cancel-button center mx1"
            rel="nofollow"
            style={{ flexGrow: 1 }}
          >
            <span>Cancel</span>
          </a>
        );
      }
    }

    let avatarNode = this._avatarNode();
    if (avatarNode) {
      avatarNode = (
        <div className="build-author-avatar" style={{ width: 32 + 8 }}>
          {avatarNode}
        </div>
      );
    }

    let authorAndTimeNode;
    if (build.authorName) {
      authorAndTimeNode = (
        <div className="build-author">
          <div>
            {build.authorName}
          </div>

          {this.timeAgoNode()}
        </div>
      );
    } else {
      authorAndTimeNode = (
        <div className="build-author flex items-center">
          {this.timeAgoNode()}
        </div>
      );
    }

    let buildMessage = (
      <BuildMessage message={build.message} />
    );
    if (build.path !== window.location.pathname) {
      buildMessage = (
        <a href={build.path} className="color-inherit hover-color-inherit">
          {buildMessage}
        </a>
      );
    }

    return (
      <div>
        {this._projectNode()}
        <div className={panelClassName}>
          <div className="panel-heading clearfix">
            <div className="flex build-header-inner">
              <div className="flex-auto build-commit">
                <h3 className="panel-title">
                  {buildMessage}
                </h3>
                <div className="build-header__build-links">
                  {this.buildInfoNode()}
                  {this.commitInfoNode()}
                </div>
              </div>
              <div className="build-info flex-none flex items-center">
                <div className="build-duration mr2">
                  <BuildDuration build={build} />
                </div>
                <div className="build-status-icon-container">
                  <div className="build-status-icon flex items-center justify-center">
                    <i className={this.icon()} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Pipeline build={build} />
          <div className="panel-footer flex flex-wrap items-center px1 py0">
            <div
              className="flex flex-wrap items-stretch overflow-hidden"
              style={{ flexGrow: 100 }}
            >
              <div className="flex items-center p2 mln1">
                {avatarNode}
                {authorAndTimeNode}
              </div>
              {this.metaInformation()}
            </div>
            {(rebuildNode || cancelNode) && (
              <div
                className="flex p2 mxn1"
                style={{ flexGrow: 1 }}
              >
                {rebuildNode}
                {cancelNode}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  _projectNode = () => {
    if (this.props.showProject) {
      return (
        <div style={{ marginBottom: 10 }}>
          <a href={this.props.build.project.url} className="dark-gray">
            {this.props.showOrganization && `${this.props.build.account.name} / `}
            {this.props.build.project.name}
          </a>
        </div>
      );
    }
  };

  _avatarNode = () => {
    if (!this.props.build.authorName) {
      return null;
    }

    const user = {
      name: this.props.build.authorName,
      avatar: { url: this.props.build.authorAvatar }
    };

    return (
      <UserAvatar
        user={user}
        style={{ width: 32, height: 32 }}
      />
    );
  };
}

export default BuildHeaderComponent;
